import { stringifyGraph } from '@/clients/http'
import { OrderModel as OrderApiClient } from '@/http/models/Order/Order'
import { format } from 'date-fns'
import { DateRange } from 'react-day-picker'

import { useFetchList } from '@/hooks/useFetchList'

import { OrderHttpParams } from '@/types/http-params/OrderHttpParams'
import { OrderModel } from '@/types/model/Order'

export interface OrderListFilters {
  search?: string
  status?: string
  payment_status?: string
  payment_method?: string
  dateRange?: DateRange
  /** `online` (default) or `pos`: the two lists are separate endpoints. */
  channel?: string
  /** POS only: the chosen branch's terminal ids, comma-separated. */
  terminal_id?: string
  /** POS only: `credit`, `returned` or `offline` (synced late). */
  pos_filter?: string
}

export const orderInitialFilters: OrderListFilters = {
  search: '',
  status: undefined,
  payment_status: undefined,
  payment_method: undefined,
  dateRange: undefined,
  channel: undefined,
  terminal_id: undefined,
  pos_filter: undefined,
}

// Select filters send `all` for "any"; the server would match it literally.
const chosen = (value?: string) => (value && value !== 'all' ? value : undefined)

/** The online list (`/order`) or the till list (`/pos-order/list`), picked by the `channel` filter. */
const listOrders = (props?: OrderHttpParams.GetList & { query?: OrderHttpParams.GetList['query'] & { channel?: string } }) => {
  const { channel, ...query } = props?.query ?? {}
  return channel === 'pos' ? OrderApiClient.getPosList({ ...props, query }) : OrderApiClient.getList({ ...props, query })
}

interface UseFetchOrdersProps {
  sort?: string | null
  shouldFetchList?: boolean
  /** `pos`: Point of sale → Orders, the till list from the start. */
  channel?: string
}

export const useFetchOrders = ({ sort, shouldFetchList = true, channel }: UseFetchOrdersProps = {}) => {
  return useFetchList<OrderListFilters, OrderModel.Order, {}, any, typeof listOrders>({
    pageSize: 10,
    initialFilters: channel ? { ...orderInitialFilters, channel } : orderInitialFilters,
    fetchFunction: listOrders,
    sort,
    register: shouldFetchList,
    buildQueryParams: (filters): Partial<OrderHttpParams.GetList['query']> & { channel?: string } => ({
      search: filters.search || undefined,
      status: chosen(filters.status),
      payment_status: chosen(filters.payment_status),
      payment_method: chosen(filters.payment_method),
      channel: chosen(filters.channel),
      terminal_id: chosen(filters.channel) === 'pos' ? chosen(filters.terminal_id) : undefined,
      pos_filter: chosen(filters.channel) === 'pos' ? chosen(filters.pos_filter) : undefined,
      start_date: filters.dateRange?.from
        ? format(filters.dateRange.from, 'yyyy-MM-dd')
        : undefined,
      end_date: filters.dateRange?.to ? format(filters.dateRange.to, 'yyyy-MM-dd') : undefined,
      graph_enc: stringifyGraph({
        '*': true,
        order_ref: true,
        quantity: true,
        total: true,
        status: true,
        payment_method: true,
        payment_status: true,
        delivered_at: true,
        order_note_id: { '*': true },
        order_source_id: { '*': true },
        terminal: { name: true, branch: { name: true } },
        staff: { name: true },
        synced_at: true,
        _timestamps: true,
      }),
    }),
    buildParams: () => ({}),
  })
}
